import { parse } from 'graphql';

import { createClientGraphQLClient } from '../graphql/client/createClientGraphQLClient';
import { GraphQLRequestError } from '../graphql/routing/apiRouting';
import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import type { AuthenticationQuery } from '../graphql/queries/authentication.generated';
import type { DashboardAuth } from './authStore';
import { normalizeDashboardAuth } from './dashboardAuth';

export type LoginCredentials = { email: string; password: string };

export const CORE_CREDENTIAL_REJECTION_MESSAGE = 'Email or password provided was invalid, or account is inactive';

// Optional client overrides — production passes nothing (real fetch + build-time
// env); tests inject `env` + `fetchImpl`. Login never carries `auth` — the
// authentication query mints the token, it doesn't require one.
export type LoginClientOverrides = {
  env: RuntimeEnvironment;
  fetchImpl?: Parameters<typeof createClientGraphQLClient>[0]['fetchImpl'];
};

// Thrown when Core rejects the credentials (vs. a network/transport failure).
export class InvalidCredentialsError extends Error {
  constructor(message = 'Invalid email or password.') {
    super(message);
    this.name = 'InvalidCredentialsError';
  }
}

// GraphQL error extension codes that mean "bad credentials" specifically (as
// opposed to FORBIDDEN/403, which on a pre-auth login request more likely means
// something else — account locked, rate-limited — and shouldn't be reported to
// the user as "check your credentials").
const AUTH_REJECTION_CODES = new Set(['UNAUTHENTICATED', 'INVALID_CREDENTIALS']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isCredentialRejectionGraphQLError = (entry: unknown): boolean => {
  if (!isRecord(entry)) return false;

  const code = isRecord(entry.extensions) ? entry.extensions.code : undefined;
  return (
    (typeof code === 'string' && AUTH_REJECTION_CODES.has(code)) ||
    entry.message === CORE_CREDENTIAL_REJECTION_MESSAGE
  );
};

// True when the request failed specifically because Core rejected the credentials,
// as opposed to a network/transport error or some other rejection reason. Reads
// GraphQLRequestError status/graphQLErrors rather than its generated wrapper message.
const isAuthRejection = (error: unknown): boolean => {
  if (!(error instanceof GraphQLRequestError)) return false;
  if (error.status === 401) return true;
  return (error.graphQLErrors ?? []).some(isCredentialRejectionGraphQLError);
};

// `authentication` is a Query (not a mutation) on Core. It returns everything
// login needs in a single call, including isBroker for role.
// NOTE: keep this selection set in sync with authentication.graphql (which drives
// the AuthenticationQuery type imported above) — codegen emits types only, not
// documents, so a change to one needs the other.
export const AUTHENTICATION_QUERY = parse(/* GraphQL */ `
  query Authentication($email: String!, $password: String!) @api(name: core) {
    authentication(email: $email, password: $password) {
      authJwt
      agentId
      brokerageId
      isBroker
    }
  }
`);

export const authApi = {
  // Authenticates against Core and returns a fully-populated DashboardAuth. Pure
  // with respect to the store — the caller commits the result to authStore.
  login: async (
    { email, password }: LoginCredentials,
    overrides: LoginClientOverrides,
  ): Promise<DashboardAuth> => {
    // The authentication query requires no auth header (it mints the token).
    const client = createClientGraphQLClient(overrides);
    let authResult: unknown;
    try {
      authResult = await client.request<AuthenticationQuery>({
        document: AUTHENTICATION_QUERY,
        variables: { email, password },
      });
    } catch (error) {
      // Bad credentials may surface as an HTTP 401 or an UNAUTHENTICATED/
      // INVALID_CREDENTIALS GraphQL error rather than a null result — classify
      // those as invalid credentials; let genuine transport failures and other
      // rejections (e.g. FORBIDDEN for a locked account) propagate as-is
      // (loginView shows a connection error for anything not classified here).
      if (isAuthRejection(error)) {
        throw new InvalidCredentialsError();
      }
      throw error;
    }

    // Core uses a null root result to reject credentials in some deployments.
    if (authResult === null) {
      throw new InvalidCredentialsError();
    }

    // A successful authentication response must be an object. Other root values
    // are malformed transport data, not a credential rejection.
    if (!isRecord(authResult)) {
      throw new Error('Authentication response data was malformed.');
    }

    // A null, missing, or partial authentication value remains a credential
    // rejection through the shared Core-to-DashboardAuth boundary mapping.
    const normalizedAuth = normalizeDashboardAuth(authResult.authentication);
    if (normalizedAuth.kind === 'invalid') {
      throw new InvalidCredentialsError();
    }

    return normalizedAuth.value;
  },
};
